// Sync module - handles offline/online synchronization
import db, { type LocalItemData } from './db';
import api, { UnauthorizedError } from './providers/supabase/api';

// Sync state
export interface SyncState {
  isOnline: boolean;
  isSyncing: boolean;
  lastSyncTime: number | null;
  pendingCount: number;
}

type SyncListener = (state: SyncState) => void;

const syncState: SyncState = {
  // Trust navigator.onLine initially - only mark offline on actual sync failures
  isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true,
  isSyncing: false,
  lastSyncTime: null,
  pendingCount: 0,
};

const listeners = new Set<SyncListener>();

// Max retries for normal (network/server) errors is 3 — see syncAll.
// Auth errors get a higher cap so the user has several chances to re-enter
// the password before queued offline work is dropped (S11).
const MAX_AUTH_RETRIES = 10;

// Store reference - will be set by useStore hook
let storeRef: {
  getState: () => { currentList: { name: string; id: string } | null };
  getAccessToken: (listName: string) => string | null;
  updateItem: (itemId: string, updates: Partial<LocalItemData>) => void;
  setSyncState: (syncState: Partial<SyncState>) => void;
} | null = null;

export function setStoreRef(store: typeof storeRef) {
  storeRef = store;
}

// Notify listeners of sync state changes
function notifySyncListeners() {
  listeners.forEach(listener => listener({ ...syncState }));
  // Also update the Zustand store
  if (storeRef) {
    storeRef.setSyncState({ ...syncState });
  }
}

// Sync module
const sync = {
  // Subscribe to sync state changes
  subscribe(listener: SyncListener): () => void {
    listeners.add(listener);
    // Immediately call with current state
    listener({ ...syncState });
    return () => listeners.delete(listener);
  },

  // Get current sync state
  getState(): SyncState {
    return { ...syncState };
  },

  // Initialize sync module
  async init(): Promise<void> {
    await db.init();

    // Listen for online/offline events
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => this.handleOnline());
      window.addEventListener('offline', () => this.handleOffline());

      // Listen for service worker sync messages
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.data.type === 'SYNC_REQUIRED') {
            this.syncAll();
          }
        });
      }
    }

    // Update pending count
    await this.updatePendingCount();

    // Notify listeners with initial state
    notifySyncListeners();

    console.log('[Sync] Initialized, online:', syncState.isOnline);
  },

  // Verify online status with actual server request
  async verifyOnline(timeout = 5000): Promise<boolean> {
    if (typeof navigator === 'undefined' || !navigator.onLine) {
      return false;
    }

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // Quick health check - just check if Supabase responds
      const response = await fetch(api.getBaseUrl(), {
        method: 'HEAD',
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok || response.status === 404 || response.status === 400) {
        // Server is reachable
        console.log('[Sync] Server reachable, marking as online');
        syncState.isOnline = true;
        notifySyncListeners();
        // Now sync pending changes
        this.syncAll();
        return true;
      }
    } catch (error) {
      console.log('[Sync] Server not reachable, staying offline:', error);
    }

    return false;
  },

  // Handle coming online
  async handleOnline(): Promise<void> {
    console.log('[Sync] Network reports online, verifying server...');
    // Don't immediately set online - verify with actual server request
    await this.verifyOnline();
  },

  // Handle going offline
  handleOffline(): void {
    console.log('[Sync] Gone offline');
    syncState.isOnline = false;
    notifySyncListeners();
  },

  // Update pending count
  async updatePendingCount(): Promise<void> {
    const queue = await db.getSyncQueue();
    syncState.pendingCount = queue.length;
    notifySyncListeners();
  },

  // Check if online
  isOnline(): boolean {
    return syncState.isOnline && (typeof navigator !== 'undefined' ? navigator.onLine : true);
  },

  // Queue an operation for sync
  async queueOperation(type: 'ADD_ITEM' | 'UPDATE_ITEM' | 'TOGGLE_ITEM' | 'DELETE_ITEM', data: Record<string, unknown>): Promise<void> {
    await db.addToSyncQueue({ type, data });
    await this.updatePendingCount();

    // Try to sync immediately if online
    if (this.isOnline() && !syncState.isSyncing) {
      this.syncAll();
    }

    // Request background sync if available
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'SyncManager' in window) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await (registration.sync as SyncManager).register('shopman-sync');
      } catch (error) {
        console.log('[Sync] Background sync not available:', error);
      }
    }
  },

  // Remove every queued operation referencing an item id.
  // Triple-review 2026-06-11 fix S8 (codex, verified): deleting a temp item
  // offline removed it locally but left its queued ADD_ITEM (and any queued
  // UPDATE/TOGGLE) in the sync queue — on reconnect the ADD re-created the
  // item on the server AND resurrected it locally via processOperation's
  // saveItem. Callers must purge the queue when deleting a never-synced item.
  async removeQueuedOpsForItem(itemId: string): Promise<void> {
    const queue = await db.getSyncQueue();
    for (const op of queue) {
      if (op.id === undefined) continue;
      const opItemId = op.data?.itemId as string | undefined;
      const addItem = op.data?.item as LocalItemData | undefined;
      if (opItemId === itemId || addItem?.id === itemId) {
        await db.removeSyncQueueItem(op.id);
      }
    }
    await this.updatePendingCount();
  },

  // Sync all pending operations
  async syncAll(): Promise<void> {
    if (syncState.isSyncing || !this.isOnline()) {
      return;
    }

    syncState.isSyncing = true;
    notifySyncListeners();

    console.log('[Sync] Starting sync...');

    try {
      const queue = await db.getSyncQueue();
      console.log('[Sync] Processing', queue.length, 'pending operations');

      for (const operation of queue) {
        try {
          await this.processOperation(operation);
          if (operation.id !== undefined) {
            await db.removeSyncQueueItem(operation.id);
          }
        } catch (error) {
          console.error('[Sync] Failed to process operation:', operation, error);

          // Auth error: api.ts already notified via IAuthProvider.notifyUnauthorized
          // so ListPage will clear the stale token + re-prompt. Stop the loop —
          // remaining ops will retry once the user re-authenticates.
          // Triple-review 2026-06-11 fix S11 (gemini, verified): previously the
          // retry counter was never bumped on auth errors, so an op that 401s
          // forever (password changed elsewhere, permission revoked) blocked the
          // head of the queue permanently. Auth errors get a generous separate
          // cap — the user keeps getting re-prompted, but after MAX_AUTH_RETRIES
          // failed sync rounds the op is dropped and the item marked 'error'.
          if (error instanceof UnauthorizedError) {
            const authRetries = (operation.retries || 0) + 1;
            if (authRetries >= MAX_AUTH_RETRIES) {
              console.error('[Sync] Max auth retries reached, removing operation:', operation);
              if (operation.id !== undefined) {
                await db.removeSyncQueueItem(operation.id);
              }
              const itemId = (operation.data?.itemId || (operation.data?.item as LocalItemData | undefined)?.id) as string | undefined;
              if (itemId) {
                const item = await db.getItem(itemId);
                if (item) {
                  item.syncStatus = 'error';
                  await db.saveItem(item);
                }
              }
            } else if (operation.id !== undefined) {
              await db.updateSyncQueueItem(operation.id, { retries: authRetries });
            }
            console.warn('[Sync] Aborting queue: UnauthorizedError. Token cleared via global event.');
            break;
          }

          // Update retry count
          const retries = (operation.retries || 0) + 1;
          if (retries >= 3) {
            // Max retries reached, mark as failed
            console.error('[Sync] Max retries reached, removing operation:', operation);
            if (operation.id !== undefined) {
              await db.removeSyncQueueItem(operation.id);
            }

            // Mark item as error if it exists
            const itemId = (operation.data?.itemId || operation.data?.id) as string | undefined;
            if (itemId) {
              const item = await db.getItem(itemId);
              if (item) {
                item.syncStatus = 'error';
                await db.saveItem(item);
              }
            }
          } else if (operation.id !== undefined) {
            // Update retry count
            await db.updateSyncQueueItem(operation.id, { retries });
          }
        }
      }

      syncState.lastSyncTime = Date.now();
      await this.updatePendingCount();
      console.log('[Sync] Sync complete');
    } catch (error) {
      console.error('[Sync] Sync failed:', error);
    } finally {
      syncState.isSyncing = false;
      notifySyncListeners();
    }
  },

  // Process a single sync operation
  async processOperation(operation: { type: string; data: Record<string, unknown> }): Promise<void> {
    const { type, data } = operation;
    const listName = storeRef?.getState().currentList?.name;
    const token = listName ? storeRef?.getAccessToken(listName) ?? null : null;

    switch (type) {
      case 'ADD_ITEM': {
        const listId = data.listId as string;
        const item = data.item as LocalItemData;
        const result = await api.addItem(listId, item, token);

        // Update local item with server ID
        if (db.isTempId(item.id)) {
          await db.deleteItem(item.id);

          // Triple-review 2026-06-11 fix S10 (codex, verified): later queued ops
          // (UPDATE/TOGGLE) created offline against the temp id were never
          // rewritten to the server id — they 404'ed on sync, burned their
          // retries and marked the item 'error'. Rewrite them now that the
          // server id exists.
          const queue = await db.getSyncQueue();
          for (const op of queue) {
            if (op.id !== undefined && op.data?.itemId === item.id) {
              await db.updateSyncQueueItem(op.id, {
                data: { ...op.data, itemId: result.id },
              });
            }
          }
        }
        await db.saveItem({ ...result, syncStatus: 'synced' });

        // Update store if this is the current list
        if (storeRef?.getState().currentList?.id === listId) {
          storeRef.updateItem(item.id, { ...result, syncStatus: 'synced' });
        }
        break;
      }

      case 'UPDATE_ITEM': {
        const itemId = data.itemId as string;
        const updates = data.updates as Partial<LocalItemData>;
        await api.updateItem(itemId, updates, token);
        await db.markItemSynced(itemId);

        // Update store
        const item = await db.getItem(itemId);
        if (item && storeRef) {
          storeRef.updateItem(itemId, { ...item, syncStatus: 'synced' });
        }
        break;
      }

      case 'TOGGLE_ITEM': {
        const itemId = data.itemId as string;
        const isBought = data.isBought as boolean;
        await api.toggleItemBought(itemId, isBought, token);
        await db.markItemSynced(itemId);

        // Update store
        if (storeRef) {
          storeRef.updateItem(itemId, { is_bought: isBought, syncStatus: 'synced' });
        }
        break;
      }

      case 'DELETE_ITEM': {
        const itemId = data.itemId as string;
        await api.deleteItem(itemId, token);
        // Item already deleted locally
        break;
      }

      default:
        console.warn('[Sync] Unknown operation type:', type);
    }
  },

  // Sync items for a specific list.
  // Triple-review 2026-06-11 fix S7 (claude+gemini, verified): items were
  // fetched via direct PostgREST (`/rest/v1/items`) with the anon key. After
  // the C1 RLS fix (migration 20260611000000) client roles can only read items
  // on open unowned lists — owned lists and view-password lists returned [].
  // Server fetch now goes through the get-list-data edge function, which
  // enforces membership / list-token / open-list access server-side. The REST
  // path remains only as fallback for legacy local lists without a slug.
  async syncListItems(list: { id: string; slug?: string }, token: string | null = null): Promise<LocalItemData[]> {
    const listId = list.id;
    if (!this.isOnline()) {
      // Return cached items sorted by user's custom order
      return db.getItemsByListSorted(listId);
    }

    try {
      // Fetch from server (edge function enforces access control)
      let serverItems: import('./providers/supabase/api').ItemData[];
      if (list.slug) {
        const resp = await api.getListData(list.slug, token);
        if (!('items' in resp) || !Array.isArray(resp.items)) {
          throw new Error(('error' in resp && resp.error) || 'get-list-data returned no items');
        }
        serverItems = resp.items;
      } else {
        serverItems = await api.getItems(listId, token);
      }

      // Get local items (with sort_order)
      const localItems = await db.getItemsByList(listId);
      const localPending = localItems.filter(item => item.syncStatus === 'pending');

      // Create a map of local sort_order values
      const sortOrderMap = new Map<string, number | undefined>();
      for (const item of localItems) {
        sortOrderMap.set(item.id, item.sort_order);
      }

      // Save server items to local DB, preserving local sort_order
      for (const item of serverItems) {
        // Don't overwrite pending local changes
        const pendingItem = localPending.find(p => p.id === item.id);
        if (!pendingItem) {
          const existingSortOrder = sortOrderMap.get(item.id);
          await db.saveItem({
            ...item,
            syncStatus: 'synced',
            sort_order: existingSortOrder,
          });
        }
      }

      // Purge local items deleted server-side (TODO.md#sync-purge-server-deleted,
      // gemini-fund verificeret 2026-06-11): et lokalt item med
      // syncStatus==='synced' som mangler i server-svaret er slettet af en
      // anden klient mens denne var offline. Uden purge bliver det i
      // IndexedDB og genopstår ved næste offline-load / fejl-fallback.
      // Konservativt: kun 'synced' purges — pending/error-items repræsenterer
      // lokalt arbejde (temp-adds eller kø-ops) og røres ikke her.
      const serverIds = new Set(serverItems.map(item => item.id));
      const idsToPurge = localItems
        .filter(item => item.syncStatus === 'synced' && !serverIds.has(item.id))
        .map(item => item.id);
      await db.batchDeleteItems(idsToPurge);

      // Return merged items (server + pending local) with sort_order preserved.
      // Triple-review 2026-06-11 fix S9 (codex, verified): the merge previously
      // returned the SERVER version for every server id — a pending local edit
      // (UPDATE/TOGGLE queued offline) survived in IndexedDB but was dropped
      // from the returned array, so the UI flashed back to the stale server
      // state until the queue flushed. Prefer the pending local version.
      const mergedItems: LocalItemData[] = serverItems.map(item => {
        const pendingItem = localPending.find(p => p.id === item.id);
        if (pendingItem) return pendingItem;
        return {
          ...item,
          syncStatus: 'synced' as const,
          lastModified: Date.now(),
          sort_order: sortOrderMap.get(item.id),
        };
      });

      // Add pending items that don't exist on server
      for (const pending of localPending) {
        if (db.isTempId(pending.id)) {
          mergedItems.push(pending);
        }
      }

      // Sort by user's custom order
      return mergedItems.sort((a, b) => {
        const aOrder = a.sort_order ?? Number.MAX_SAFE_INTEGER;
        const bOrder = b.sort_order ?? Number.MAX_SAFE_INTEGER;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
    } catch (error) {
      console.error('[Sync] Failed to sync list items:', error);
      // Return cached items on error, sorted by user's custom order
      return db.getItemsByListSorted(listId);
    }
  },

  // S11-opfølgning (triple-review-deferred, 2026-06-11): nulstil retry-
  // tællere efter succesfuld re-auth. Uden reset tæller gamle 401-runder
  // mod MAX_AUTH_RETRIES som en livstids-cap — en bruger der re-auther
  // korrekt efter 9 fejlede runder ville få droppet offline-arbejde ved
  // næste enkeltstående fejl. Kaldes fra password-success-flowet.
  async resetAuthRetries(): Promise<void> {
    const queue = await db.getSyncQueue();
    const reset = queue
      .filter(op => op.id !== undefined && (op.retries || 0) > 0)
      .map(op => ({ ...op, retries: 0 }));
    await db.batchPutSyncQueue(reset);
  },

  // Save list to local DB
  async saveList(list: Parameters<typeof db.saveList>[0]): Promise<void> {
    await db.saveList(list);
  },

  // Get list from local DB
  async getLocalList(name: string) {
    return db.getListByName(name);
  },
};

// Type declaration for SyncManager
declare global {
  interface SyncManager {
    register(tag: string): Promise<void>;
  }
  interface ServiceWorkerRegistration {
    sync: SyncManager;
  }
}

export default sync;
